import { AtlasChild, Content } from '@23degrees/shared-code/models';
import { StyleService } from '@23degrees/shared/services/style/style.service';
import { NgStyle } from '@angular/common';
import { Component, ElementRef, EventEmitter, Output, SimpleChanges, ViewChild, computed } from '@angular/core';
import { LayoutElementComponent } from '../layout-element/layout-element.component';

@Component({
  selector: 'layout-navigation',
  templateUrl: './layout-navigation.component.html',
  styleUrls: ['./layout-navigation.component.scss'],
  imports: [NgStyle],
})
export class LayoutNavigationComponent extends LayoutElementComponent {
  @Output() navEvent = new EventEmitter<any>();

  @ViewChild('navContainer') navContainer!: ElementRef<HTMLElement>;

  public children: AtlasChild[] = [];
  public hoverNav: boolean[] = [];
  public activeChildIdx: number = 0;

  private isNavClicked: boolean = false;
  private isNavMoved: boolean = false;

  private scrollStart: number = 0;
  private scrollLeft: number = 0;

  ngAfterViewInit() {
    const { nativeElement } = this.navContainer;

    nativeElement.addEventListener('mousedown', this.navStart);
    nativeElement.addEventListener('touchstart', this.navStart);

    nativeElement.addEventListener('mousemove', this.navMove);
    nativeElement.addEventListener('touchmove', this.navMove);
    nativeElement.addEventListener('wheel', this.navWheel);

    nativeElement.addEventListener('mouseleave', this.navEnd);
    nativeElement.addEventListener('mouseup', this.navEnd);
    nativeElement.addEventListener('touchend', this.navEnd);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.content().typeSpecific.atlas) {
      this.children = this.content().typeSpecific.atlas.children;
      this.hoverNav = this.children.map(() => false);

      this.activeChildIdx = this.children.findIndex((child) => child.slug === this.config().activeChild.slug);

      const childElement = this.navContainer?.nativeElement?.children[this.activeChildIdx];
      const parentElement = this.navContainer?.nativeElement?.parentElement;

      if (childElement && parentElement) {
        const scrollWidth = parentElement.scrollWidth - parentElement.clientWidth;
        const scrollOffset = Math.floor((scrollWidth / (this.children.length - 1)) * this.activeChildIdx);

        parentElement.scrollTo({ left: scrollOffset, behavior: 'smooth' });
        // childElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      }
    }
  }

  public navClick = (click: { idx: number; child: AtlasChild; event: MouseEvent }) => {
    const { idx, child, event } = click;

    if (this.isNavMoved) {
      event.preventDefault();
      return;
    }

    this.activeChildIdx = idx;

    const childElement = this.navContainer?.nativeElement?.children[this.activeChildIdx];
    const parentElement = this.navContainer?.nativeElement?.parentElement;

    if (childElement && parentElement) {
      const scrollWidth = parentElement.scrollWidth - parentElement.clientWidth;
      const scrollOffset = Math.floor((scrollWidth / (this.children.length - 1)) * this.activeChildIdx);

      parentElement.scrollTo({ left: scrollOffset, behavior: 'smooth' });
      // childElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    }

    this.navEvent.emit({ type: 'atlas-idx', idx: idx, stayOpen: false });
  };

  private navStart = (event: MouseEvent & TouchEvent) => {
    this.isNavClicked = true;
    this.isNavMoved = false;

    if (this.navContainer?.nativeElement) {
      this.navContainer.nativeElement.classList.add('active');

      this.scrollStart = event.pageX || event.touches[0].pageX - this.navContainer.nativeElement.parentElement.offsetLeft;
      this.scrollLeft = this.navContainer.nativeElement.parentElement.scrollLeft;
    }
  };

  private navMove = (event: MouseEvent & TouchEvent) => {
    if (!this.isNavClicked || !this.navContainer?.nativeElement) return;

    const x = event.pageX || event.touches[0].pageX - this.navContainer.nativeElement.parentElement.offsetLeft;
    const dist = x - this.scrollStart;

    if (Math.abs(dist) > 10) {
      this.isNavMoved = true;
      event.preventDefault();
    }

    this.navContainer.nativeElement.parentElement.scrollLeft = this.scrollLeft - dist;
  };

  private navWheel = (event: WheelEvent) => {
    if (!this.navContainer?.nativeElement) return;

    event.preventDefault();

    this.navContainer.nativeElement.parentElement.scrollLeft += event.deltaY;
  };

  private navEnd = (event: MouseEvent) => {
    this.isNavClicked = false;

    if (this.navContainer?.nativeElement) {
      this.navContainer.nativeElement.classList.remove('active');
    }
  };

  public getNavContainerStyle = computed(() => {
    // content gets the update signal, data is stored in layout -> child options
    const content = this.content();

    const { overflow, useGrid, gridGap } = this.childOptions();

    const style: any = {
      display: useGrid ? 'grid' : 'flex',
      flexShrink: overflow ? 0 : 1,
    };

    if (useGrid) {
      style.gridGap = `${gridGap}px`;
      style.gridAutoFlow = 'column';
    } else {
      style.flexDirection = 'row';
    }

    return style;
  });

  public getNavStyle = (idx: number) => {
    const { overflow, useGrid, gridGap } = this.childOptions();

    let style = {
      ...this.responsiveStylePipe.transform(this.childOptions().font),
      ...this.childOptions().background,
      borderRadius: `${this.childOptions().borderRadius}px`,
      boxShadow: this.responsiveStylePipe.transform(this.childOptions().boxShadow, 'shadow'),
    };

    if (this.activeChildIdx === idx) {
      style = {
        ...this.responsiveStylePipe.transform(this.childOptions().activeFont),
        ...this.childOptions().activeBackground,
        borderRadius: `${this.childOptions().borderRadius}px`,
        boxShadow: this.responsiveStylePipe.transform(this.childOptions().activeBoxShadow, 'shadow'),
      };
    }

    if (this.hoverNav[idx]) {
      style = {
        ...this.responsiveStylePipe.transform(this.childOptions().hoverFont),
        ...this.childOptions().hoverBackground,
        borderRadius: `${this.childOptions().borderRadius}px`,
        boxShadow: this.responsiveStylePipe.transform(this.childOptions().hoverBoxShadow, 'shadow'),
      };
    }

    if (useGrid) {
      const outlineSize = Math.ceil(gridGap / 2);
      // style.outline = outlineSize + 'px solid black';
    }

    const { padding, margin } = style;
    const paddingTop = `${padding.top}${padding.topUnit}`;
    const paddingBottom = `${padding.bottom}${padding.bottomUnit}`;
    const marginTop = `${margin.top}${margin.topUnit}`;
    const marignBottom = `${margin.bottom}${margin.bottomUnit}`;

    style.maxHeight = `calc(1em + ${paddingTop} + ${paddingBottom} + ${marginTop} + ${marignBottom})`;

    return style;
  };
}
